/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import axios from "axios";
import toast from "react-hot-toast";
import { API_URL } from "../config";

// ─── Renk Paleti ─────────────────────────────────────────────────────────────
const C = {
    bg:       "#0f172a",
    panel:    "#1e293b",
    border:   "#334155",
    text:     "#e2e8f0",
    muted:    "#94a3b8",
    accent:   "#3b82f6",
    bullish:  "#16a34a",
    bearish:  "#dc2626",
    warning:  "#f59e0b",
    gold:     "#eab308",
    purple:   "#7c3aed",
    teal:     "#0d9488",
};

// ─── Token ──────────────────────────────────────────────────────────────────
async function getToken() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;
    if (session.expires_at * 1000 - Date.now() < 5 * 60 * 1000) {
        const { data } = await supabase.auth.refreshSession();
        return data.session?.access_token;
    }
    return session.access_token;
}

// ─── Modül Etiketi ────────────────────────────────────────────────────────────
const MODUL_ETIKET = {
    fvg:           "FVG",
    msb:           "MSB",
    ote:           "OTE",
    obbb:          "OB/BB",
    likidite:      "Likidite",
    smt:           "SMT",
    pd:            "PD Zone",
    capa:          "Çapa",
    killzone:      "Killzone",
    divergence:    "Divergence",
    hacim_profili: "Hacim Profili",
    hacim_akisi:   "Hacim Akışı",
    inducement:    "IDM",
    karar_motoru:  "Karar Motoru",
    puanlama:      "Puanlama",
    coklu_tarama:  "Çoklu Tarama",
    dip:           "Dip Tarama",
    topdown:       "Topdown",
    islem:         "İşlem Planlama",
};

// ─── Ağırlık Matrisi Satırı ───────────────────────────────────────────────────
function AgirlikSatiri({ modul, katsayi, kayitSayisi, son365, simKayit }) {
    const abs    = Math.abs(katsayi);
    const maxAbs = 3;
    const pozitif = katsayi >= 0;
    const renk   = pozitif ? C.bullish : C.bearish;
    const pct    = Math.min((abs / maxAbs) * 100, 100);
    const etiket = MODUL_ETIKET[modul] || modul;

    return (
        <tr style={{ borderBottom: `1px solid ${C.border}` }}>
            <td style={{ padding: "10px 12px", color: C.text, fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" }}>
                {etiket}
            </td>
            <td style={{ padding: "10px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 160, height: 8, background: C.bg, borderRadius: 4, overflow: "hidden", border: `1px solid ${C.border}` }}>
                        <div style={{
                            height: "100%", width: `${pct}%`,
                            background: renk, borderRadius: 4, transition: "width 0.4s",
                        }} />
                    </div>
                    <span style={{ color: renk, fontWeight: 700, fontSize: 13, minWidth: 52 }}>
                        {katsayi > 0 ? "+" : ""}{katsayi?.toFixed(3)}
                    </span>
                </div>
            </td>
            <td style={{ padding: "10px 12px", textAlign: "right" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-end" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ fontSize: 9, color: C.teal, background: C.teal + "22",
                            padding: "1px 5px", borderRadius: 4, fontWeight: 600 }}>DA</span>
                        <span style={{ fontSize: 12, color: C.muted }}>
                            {kayitSayisi?.toLocaleString("tr-TR") ?? "—"}
                        </span>
                    </div>
                    {son365 > 0 && son365 !== kayitSayisi && (
                        <div style={{ fontSize: 9, color: C.muted }}>
                            son 1 yıl: {son365.toLocaleString("tr-TR")}
                        </div>
                    )}
                    {simKayit > 0 && (
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <span style={{ fontSize: 9, color: C.purple, background: C.purple + "22",
                                padding: "1px 5px", borderRadius: 4, fontWeight: 600 }}>SIM</span>
                            <span style={{ fontSize: 12, color: C.muted }}>
                                {simKayit.toLocaleString("tr-TR")}
                            </span>
                        </div>
                    )}
                </div>
            </td>
            <td style={{ padding: "10px 12px" }}>
                <span style={{
                    fontSize: 11, padding: "2px 10px", borderRadius: 20, fontWeight: 600,
                    background: pozitif ? C.bullish + "22" : C.bearish + "22",
                    color: renk,
                }}>
                    {pozitif ? "ALIŞA KATKI" : "SATIŞA KATKI"}
                </span>
            </td>
        </tr>
    );
}

// ─── Bilgi Kartı Tooltip (sadece ağırlık matrisi/model eğitimi ile ilgili anahtarlar) ──
const BILGI_ICERIK = {
    ml_katsayisi: {
        baslik: "ML Katsayısı",
        aciklama: "Logistic Regression modelinin her modüle verdiği ağırlık. Pozitif değer o modülün alış sinyalini desteklediğini, negatif değer ise satış yönüne katkıda bulunduğunu gösterir. Büyük mutlak değer = daha güçlü etki.",
    },
    cv_skoru: {
        baslik: "CV Skoru (5-Fold)",
        aciklama: "Modelin 5 farklı veri diliminde test edilmesiyle elde edilen ortalama doğruluk oranı. %70 üzeri iyi, %80 üzeri çok iyi. Eğitim verisi az olduğunda düşük çıkabilir.",
    },
    egitim_orneklemi: {
        baslik: "Eğitim Örneği",
        aciklama: "Modelin eğitildiği sinyal kayıt sayısı. Tüm doğruluk tablolarından toplanan tarihsel sinyal verileridir. Daha fazla örnek = daha güvenilir model. Minimum 30 kayıt gerekir.",
    },
};

function BilgiKarti({ karti, kapat }) {
    if (!karti || !BILGI_ICERIK[karti]) return null;
    const { baslik, aciklama } = BILGI_ICERIK[karti];
    return (
        <div style={{
            position: "fixed", inset: 0, zIndex: 999,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(0,0,0,0.6)",
        }} onClick={kapat}>
            <div style={{
                background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14,
                padding: 28, maxWidth: 420, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
            }} onClick={e => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: C.text }}>ℹ️ {baslik}</span>
                    <button onClick={kapat} style={{ background: "none", border: "none", color: C.muted, fontSize: 20, cursor: "pointer" }}>✕</button>
                </div>
                <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, margin: 0 }}>{aciklama}</p>
            </div>
        </div>
    );
}

function BilgiIkonu({ karti, setBilgiKarti }) {
    return (
        <span onClick={() => setBilgiKarti(karti)} title="Bilgi"
            style={{ marginLeft: 4, fontSize: 11, color: C.muted, cursor: "pointer",
                     borderRadius: "50%", border: `1px solid ${C.border}`,
                     padding: "0 4px", lineHeight: "16px", display: "inline-block",
                     userSelect: "none" }}>ℹ</span>
    );
}

// ─── Model Eğitim İlerleme Paneli ────────────────────────────────────────────
function EgitimIlerlemePaneli({ durum, mod }) {
    const gecen  = durum.gecen_sure_frontend || durum.gecen_sure || 0;
    const adim   = durum.adim        || 0;
    const toplam = durum.toplam_adim || 20;  // dinamik — backend'den gelir
    const pct    = toplam > 0 ? Math.round((adim / toplam) * 100) : 0;
    const mesaj  = durum.mesaj || "İşleniyor...";

    // Son 4 adım sabit (sınıf ağırlığı, CV, eğit, kaydet)
    const sonDortAdim = toplam - 4;

    let tahminiKalan = null;
    if (adim > 0 && gecen > 0) {
        if (adim <= sonDortAdim) {
            const snPerAdim = gecen / adim;
            const kalanAdim = toplam - adim;
            const kalanModulAdim = Math.max(0, sonDortAdim - adim);
            const kalanSabitAdim = Math.min(4, kalanAdim);
            tahminiKalan = Math.round(snPerAdim * kalanModulAdim + kalanSabitAdim * 5);
        } else {
            const kalanAdim = toplam - adim;
            tahminiKalan = kalanAdim * 5;
        }
    }

    const formatSure = (sn) => {
        if (sn == null || sn < 0) return "—";
        const h = Math.floor(sn / 3600);
        const m = Math.floor((sn % 3600) / 60);
        const s = sn % 60;
        if (h > 0) return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
        return `${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
    };

    const faslar = adim <= sonDortAdim
        ? `Modül Tarama (${adim}/${sonDortAdim})`
        : ["Sınıf Ağırlıkları", "Cross Validation", "Model Eğitimi", "Kaydediliyor"][adim - sonDortAdim - 1] || "Tamamlanıyor";

    return (
        <div style={{
            marginBottom: 16, background: C.panel,
            border: `2px solid ${C.purple}44`, borderRadius: 12, padding: 20,
            boxShadow: `0 0 20px ${C.purple}22`,
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <span style={{ fontSize: 20 }}>🤖</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>
                    Model Eğitimi — {mod}
                </span>
                <span style={{ marginLeft: "auto", fontSize: 26, fontWeight: 900, color: C.purple }}>
                    %{pct}
                </span>
            </div>

            <div style={{ height: 10, background: C.bg, borderRadius: 6, overflow: "hidden",
                marginBottom: 8, border: `1px solid ${C.border}` }}>
                <div style={{
                    height: "100%", width: `${pct}%`,
                    background: `linear-gradient(90deg, ${C.purple}, ${C.accent})`,
                    borderRadius: 6, transition: "width 0.8s ease",
                }} />
            </div>

            <div style={{ display: "flex", justifyContent: "space-between",
                marginBottom: 8, fontSize: 11 }}>
                <span style={{ color: C.purple, fontWeight: 700 }}>{faslar}</span>
                <span style={{ color: C.muted }}>{adim} / {toplam} adım</span>
            </div>

            <div style={{ fontSize: 12, color: C.muted, marginBottom: 14,
                minHeight: 18, fontFamily: "monospace", background: C.bg,
                padding: "4px 8px", borderRadius: 4, border: `1px solid ${C.border}` }}>
                {mesaj}
            </div>

            <div style={{ display: "flex", gap: 20 }}>
                <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>GEÇEN SÜRE</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.teal }}>{formatSure(gecen)}</div>
                </div>
                <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>TAHMİNİ KALAN</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.gold }}>
                        {tahminiKalan != null ? formatSure(tahminiKalan) : "—"}
                    </div>
                </div>
                <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>ORT. ADIM SÜRESİ</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.muted }}>
                        {adim > 0 ? formatSure(Math.round(gecen / adim)) : "—"}
                    </div>
                </div>
            </div>

            <div style={{ display: "flex", gap: 6, marginTop: 14 }}>
                {["Sınıf Ağırlık", "CV", "Eğitim", "Kaydet"].map((etiket, i) => {
                    const adimNo    = sonDortAdim + i + 1;
                    const tamam     = adim >= adimNo;
                    const aktif     = adim === adimNo - 1 && adim > sonDortAdim - 1;
                    const renk      = tamam ? C.bullish : aktif ? C.purple : C.border;
                    return (
                        <div key={i} style={{ flex: 1, textAlign: "center" }}>
                            <div style={{ height: 4, borderRadius: 2, background: renk,
                                marginBottom: 4, transition: "background 0.3s" }} />
                            <div style={{ fontSize: 9, color: tamam ? C.bullish : C.muted,
                                fontWeight: tamam ? 700 : 400 }}>
                                {tamam ? "✓ " : ""}{etiket}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ─── Ana Bileşen ─────────────────────────────────────────────────────────────
export default function ModulAgirlikMerkezi() {
    const [mod,              setMod]              = useState("BIST");
    const [matris,           setMatris]            = useState(null);
    const [bilgiKarti,       setBilgiKarti]        = useState(null);
    const [matrisAcik,       setMatrisAcik]        = useState(true);

    const [egitimYukleniyor, setEgitimYukleniyor]  = useState(false);
    const [egitimDurum,      setEgitimDurum]       = useState(null); // {adim,toplam_adim,mesaj,gecen_sure,calisiyor}
    const egitimPollRef = useRef(null);
    const egitimBasRef  = useRef(null);

    // Ağırlık matrisini yükle
    useEffect(() => {
        matrisYukle(mod);
    }, [mod]);

    // Interval temizleme — component unmount
    useEffect(() => () => {
        if (egitimPollRef.current) clearInterval(egitimPollRef.current);
    }, []);

    async function matrisYukle(m) {
        try {
            const token = await getToken();
            const res   = await axios.get(
                `${API_URL}/scan/birlesik-skor/agirlik-matrisi?mod=${m}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setMatris(res.data);
        } catch (e) {
            console.warn("Ağırlık matrisi yüklenemedi:", e.message);
        }
    }

    // ─── Model Eğit (Admin) ───────────────────────────────────────────────────
    async function modelEgit() {
        if (egitimYukleniyor) return;
        setEgitimYukleniyor(true);
        setEgitimDurum({ adim: 0, toplam_adim: 6, mesaj: "Başlatılıyor...", gecen_sure: 0, calisiyor: true });
        egitimBasRef.current = Date.now();
        try {
            const token = await getToken();
            await axios.post(
                `${API_URL}/scan/birlesik-skor/model-egit?mod=${mod}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (egitimPollRef.current) clearInterval(egitimPollRef.current);
            egitimPollRef.current = setInterval(async () => {
                try {
                    const t   = await getToken();
                    const res = await axios.get(`${API_URL}/scan/birlesik-skor/egitim-durum`, {
                        headers: { Authorization: `Bearer ${t}` },
                    });
                    const d = res.data;
                    setEgitimDurum({
                        ...d,
                        gecen_sure_frontend: Math.round((Date.now() - egitimBasRef.current) / 1000),
                    });
                    if (!d.calisiyor) {
                        clearInterval(egitimPollRef.current);
                        setEgitimYukleniyor(false);
                        if (d.hata) {
                            toast.error("Eğitim hatası: " + d.hata);
                        } else {
                            toast.success(d.mesaj || "Model eğitimi tamamlandı!");
                            matrisYukle(mod);
                        }
                    }
                } catch {}
            }, 2000);
        } catch (e) {
            toast.error("Eğitim başlatılamadı: " + e.message);
            setEgitimYukleniyor(false);
            setEgitimDurum(null);
        }
    }

    // ─── Ağırlık Matrisi — katsayıları sırala ────────────────────────────────
    const agirlikSatirlar = (() => {
        if (!matris?.model?.agirliklar) return [];
        const ag = matris.model.agirliklar;
        return Object.entries(ag)
            .filter(([k]) => k.endsWith("_aktif"))
            .map(([k, v]) => {
                const key = k.replace("_aktif", "");
                const mb  = matris.modul_basari?.[key];
                return {
                    modul:     key,
                    katsayi:   v,
                    kayit:     mb?.toplam_kayit ?? 0,
                    son365:    mb?.son365_kayit ?? 0,
                    sim:       mb?.sim_kayit ?? 0,
                };
            })
            .sort((a, b) => Math.abs(b.katsayi) - Math.abs(a.katsayi));
    })();

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div style={{ padding: 24, background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "system-ui,sans-serif" }}>
            {bilgiKarti && <BilgiKarti karti={bilgiKarti} kapat={() => setBilgiKarti(null)} />}

            {/* Başlık */}
            <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:24}}>
                <span style={{fontSize:32}}>⚖️</span>
                <div>
                    <h1 style={{margin:0,fontSize:22,fontWeight:800,color:C.text}}>Modül Ağırlık Merkezi</h1>
                    <p style={{margin:0,fontSize:13,color:C.muted}}>Birleşik Skor ML modelinin ağırlık matrisi ve eğitimi · {mod}</p>
                </div>
            </div>

            {/* Piyasa Seçici */}
            <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
                {["BIST", "Kripto"].map(m => (
                    <button key={m} onClick={() => setMod(m)} disabled={egitimYukleniyor} style={{
                        padding: "8px 20px", borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: "pointer",
                        border: `2px solid ${mod === m ? C.purple : C.border}`,
                        background: mod === m ? C.purple + "22" : "transparent",
                        color: mod === m ? C.purple : C.muted,
                    }}>{m}</button>
                ))}
            </div>

            {/* Model Eğitim İlerleme Paneli */}
            {egitimDurum && egitimDurum.calisiyor && (
                <EgitimIlerlemePaneli durum={egitimDurum} mod={mod} />
            )}

            {/* AĞIRLIK MATRİSİ */}
            {matris ? (
                <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
                    {/* Başlık */}
                    <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}`, background: C.bg,
                        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
                         onClick={() => setMatrisAcik(v => !v)}>
                            <span style={{ fontSize: 16 }}>⚖️</span>
                            <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Modül Ağırlık Matrisi</span>
                            <span style={{ fontSize: 11, color: C.muted }}>— ML Logistic Regression Katsayıları</span>
                            <span style={{ fontSize: 12, color: C.muted, marginLeft: 4 }}>{matrisAcik ? "▲" : "▼"}</span>
                            <button onClick={(e) => { e.stopPropagation(); modelEgit(); }}
                                disabled={egitimYukleniyor}
                                style={{ marginLeft: 12, padding: "3px 12px", borderRadius: 6, fontSize: 11,
                                    cursor: "pointer", border: `1px solid ${C.border}`,
                                    background: C.panel, color: C.muted, fontWeight: 600 }}>
                                {egitimYukleniyor ? "⏳" : "🤖 Modeli Eğit"}
                            </button>
                        </div>
                        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                            {matris.model?.cv_skoru != null && (
                                <div style={{ textAlign: "right" }}>
                                    <div style={{ fontSize: 10, color: C.muted }}>CV Skoru (5-Fold)<BilgiIkonu karti="cv_skoru" setBilgiKarti={setBilgiKarti} /></div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: C.teal }}>
                                        %{(matris.model.cv_skoru * 100).toFixed(1)}
                                    </div>
                                </div>
                            )}
                            {matris.model?.orneklem != null && (
                                <div style={{ textAlign: "right" }}>
                                    <div style={{ fontSize: 10, color: C.muted }}>Eğitim Örneği<BilgiIkonu karti="egitim_orneklemi" setBilgiKarti={setBilgiKarti} /></div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: C.accent }}>
                                        {matris.model.orneklem?.toLocaleString("tr-TR")}
                                    </div>
                                </div>
                            )}
                            {matris.model?.egitim_tarihi && (
                                <div style={{ textAlign: "right" }}>
                                    <div style={{ fontSize: 10, color: C.muted }}>Son Eğitim</div>
                                    <div style={{ fontSize: 12, color: C.muted }}>{matris.model.egitim_tarihi}</div>
                                </div>
                            )}
                            <div style={{ textAlign: "right" }}>
                                <div style={{ fontSize: 10, color: C.muted }}>DA Kayıt</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: C.teal }}>
                                    {Object.values(matris.modul_basari || {})
                                        .reduce((s, m) => s + (m.toplam_kayit || 0), 0)
                                        .toLocaleString("tr-TR")}
                                </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                                <div style={{ fontSize: 10, color: C.muted }}>SIM Kayıt</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: C.purple }}>
                                    {Object.values(matris.modul_basari || {})
                                        .reduce((s, m) => s + (m.sim_kayit || 0), 0)
                                        .toLocaleString("tr-TR")}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tablo — sadece matrisAcik=true ise göster */}
                    {matrisAcik && agirlikSatirlar.length > 0 && (
                        <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                <thead>
                                    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                                        {[
                                            { label: "Modül",        karti: null },
                                            { label: "ML Katsayısı", karti: "ml_katsayisi" },
                                            { label: "Kayıt Sayısı", karti: null },
                                            { label: "Etki Yönü",   karti: null },
                                        ].map(({ label, karti }) => (
                                            <th key={label} style={{ padding: "10px 12px", textAlign: "left",
                                                color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.03em" }}>
                                                {label}{karti && <BilgiIkonu karti={karti} setBilgiKarti={setBilgiKarti} />}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {agirlikSatirlar.map(s => (
                                        <AgirlikSatiri key={s.modul} modul={s.modul} katsayi={s.katsayi} kayitSayisi={s.kayit} son365={s.son365} simKayit={s.sim} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {matrisAcik && agirlikSatirlar.length === 0 && (
                        <div style={{ padding: 32, textAlign: "center", color: C.muted }}>
                            <div style={{ fontSize: 32, marginBottom: 8 }}>🤖</div>
                            <div style={{ fontSize: 14, marginBottom: 4 }}>Henüz eğitilmiş model yok</div>
                            <div style={{ fontSize: 12 }}>Yeterli doğruluk verisi birikmesi gerekiyor (min 30 kayıt)</div>
                        </div>
                    )}
                </div>
            ) : (
                <div style={{ textAlign: "center", padding: 60, color: C.muted }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>⚖️</div>
                    <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: C.text }}>
                        Modül Ağırlık Merkezi
                    </div>
                    <div style={{ fontSize: 13, maxWidth: 480, margin: "0 auto", lineHeight: 1.6 }}>
                        Birleşik Skor modelinin her tarama modülüne verdiği ML ağırlıklarını burada
                        görüntüleyip modeli yeniden eğitebilirsin. Sembol skorlarını görmek/tarama
                        yapmak için ayrı "Birleşik Skor" sayfasını kullan.
                    </div>
                </div>
            )}
        </div>
    );
}
